import React, { Fragment, useState, useEffect, createRef, useRef } from 'react';
import { findDOMNode } from 'react-dom';
import { Resizable } from 'react-resizable';
import { useTranslation } from "react-i18next";
import {
    getColumnVisibleConfigs, setColumnVisibleConfigs, clearColumnVisibleConfigs,
    getColumnRenderConfigs,
    getColumnResizeDisabledConfigs,
    setColumnResizeDisabledConfigs
} from '../../../../../utils/commonUtils';
import { PAGINATION } from '../../../../../constants/Define';
import { Table as AntTable, Checkbox, Button, Icon, Empty, Tooltip, Drawer, Switch, Modal } from 'antd';
import { merge, findIndex, countBy, throttle, orderBy } from 'lodash'
import FontIcon, { SolidIcons } from './../FontIcon';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import $ from 'jquery';
import classNames from 'classnames';

import './Table.less';

const EXPAND_COLUMN_WIDTH = 50;
const MIN_COLUMN_WIDTH = 80;
const MAX_AUTO_COLUMN_WIDTH = 500;
const AUTO_COLUMN_PADDING = 16;

const getColumnKey = column => column.dataIndex !== undefined ? column.dataIndex : column.key;

const getResizableScroll = (scroll, columns, hasExpandedRows) => {
    if (!scroll || typeof scroll.x !== 'number') {
        return scroll;
    }

    const columnWidths = columns.map(column => Number(column.width));
    if (!columnWidths.length || columnWidths.some(width => !Number.isFinite(width))) {
        return scroll;
    }

    return {
        ...scroll,
        x: columnWidths.reduce((total, width) => total + width, 0) + (hasExpandedRows ? EXPAND_COLUMN_WIDTH : 0),
    };
};

const getAutoColumnWidth = element => {
    const clonedElement = $(element).clone();
    clonedElement.css({
        position: 'absolute',
        visibility: 'hidden',
        width: 'auto',
        minWidth: 0,
        maxWidth: 'none',
        height: 'auto',
        whiteSpace: 'nowrap',
    });
    clonedElement.appendTo(document.body);
    const width = Math.ceil(clonedElement.outerWidth()) + AUTO_COLUMN_PADDING;
    clonedElement.remove();
    return Math.min(Math.max(width, MIN_COLUMN_WIDTH), MAX_AUTO_COLUMN_WIDTH);
};

const Table = (props) => {

    const { t } = useTranslation();
    const { hideSettings } = props;
    const components = merge(props.components || {}, {
        header: {
            cell: ResizeableTableTitle,
        },
    });

    const onChange = (pagination, filters, sorter) => {
        props.onChange(pagination, filters, sorter);
    };
    const [columns, setColumns] = useState(props.columns || []);
    const [isResizeDisabled, setResizeDisabled] = useState(false);
    const [dragColumn, setDragColumn] = useState(null);
    const tableRef = useRef(null);
    const columnsRef = useRef(props.columns || []);

    useEffect(() => {
        const tableKey = getTableKey();
        const cols = tableKey ? getColumnVisibleConfigs(tableKey, props.columns) : props.columns;
        setColumnVisible(cols, isResizeDisabled);
    }, [props.columns])

    const setColumnVisible = (cols, unresizable = isResizeDisabled) => {
        const next = getColumnRenderConfigs(cols, unresizable ? undefined : onColumnResize);
        columnsRef.current = next;
        return setColumns(next);
    }
    const saveResizeDisabledConfig = (checked) => {
        const tableKey = getTableKey();
        setResizeDisabled(checked);
        if (tableKey) {
            setColumnResizeDisabledConfigs(tableKey, checked)
        }
        setColumnVisible(columns, checked)
    }
    const saveColumnConfig = (columnConfigs) => {
        const tableKey = getTableKey();
        if (tableKey) {
            setColumnVisibleConfigs(tableKey, columnConfigs);
        }
    }
    const onColumnResize = (column, index) => throttle((e, { size, allWidth = [], isStop = false, isStart = false }) => {
        if (isStart && Object.keys(allWidth).length) {
            columns.forEach((col) => {
                const columnKey = getColumnKey(col);
                if (allWidth[columnKey] >= 0) {
                    col.width = col.visible === false ? 0 : allWidth[columnKey];
                }
            })
        } else if (isStop) {
            saveColumnConfig(columns);
        } else {
            columns[index].width = Math.max(size.width, MIN_COLUMN_WIDTH);
        }

        setColumnVisible(columns, false);
    }, 100)
    const onColumnVisibleChange = (column, checked) => {
        const checkedColumnIndex = findIndex(columns, item => item.dataIndex === column.dataIndex);
        if (checkedColumnIndex !== -1) {
            const columnConfigs = columns;
            columnConfigs[checkedColumnIndex].visible = checked;
            setColumnVisible(columnConfigs)
            saveColumnConfig(columnConfigs)
            return props.onColumnVisibleChange && props.onColumnVisibleChange(column, checked)
        }
    }
    const getTableKey = () => {
        return props.tableKey || columns.map(col => col.dataIndex).join('|');
    }
    const [configVisibled, setConfigVisible] = useState(false);
    const pagination = props.pagination && !(props.pagination.total <= PAGINATION.pageSize ) ? props.pagination : false;
    if (pagination) {
        pagination.showTotal = (total, range) => `${range[0]}-${range[1]} ${t(`paginate.of`)} ${total} ${t(`paginate.items`)}`;
        pagination.showSizeChanger = true;
        pagination.showQuickJumper = true;
        pagination.hideOnSinglePage = false;
        pagination.size = 'middle';
        pagination.locale = {
            items_per_page: t(`paginate.itemsPerPage`),
            jump_to: t(`paginate.jumpTo`),
        }
    }

    const onCloseSettings = () => {
        setConfigVisible(!configVisibled);
    }

    const onResetSetting = () => {
        const { confirm } = Modal
        confirm({
            title: t(`validation.sureResetSetting`),
            okText: t(`action.reset`),
            cancelText: t(`action.cancel`),
            onOk: () => {
                const tableKey = getTableKey();
                if (tableKey) {
                    clearColumnVisibleConfigs(tableKey);
                }
                saveResizeDisabledConfig(false);
                const cols = (props.columns || []).map(({ order, ...rest }) => ({
                    ...rest,
                    visible: true,
                }));
                setColumnVisible(cols, false);
                setTimeout(() => setConfigVisible(false), 300);
            },
        });

    }

    const onAutoResize = () => {
        const tableWrapper = $(tableRef.current).find('.ant-table-wrapper').first().get(0);
        if (!tableWrapper) return;

        const columnWidths = {};
        const rows = $(tableWrapper).find('.ant-table-tbody > tr').filter(function () {
            return !$(this).hasClass('ant-table-expanded-row') && $(this).closest('.ant-table-wrapper').get(0) === tableWrapper;
        });
        $(tableWrapper).find('.ant-table-thead th[data-column-key]').filter(function () {
            return $(this).closest('.ant-table-wrapper').get(0) === tableWrapper;
        }).each(function () {
            const columnKey = $(this).attr('data-column-key');
            const columnIndex = this.cellIndex;
            let width = getAutoColumnWidth(this);
            rows.each(function () {
                const cell = this.cells && this.cells.item(columnIndex);
                if (cell) {
                    width = Math.max(width, getAutoColumnWidth(cell));
                }
            });
            columnWidths[columnKey] = Math.min(width, MAX_AUTO_COLUMN_WIDTH);
        });

        const resizedColumns = columns.map(column => {
            const columnKey = getColumnKey(column);
            return columnWidths[columnKey] ? { ...column, width: columnWidths[columnKey] } : column;
        });
        setColumnVisible(resizedColumns, isResizeDisabled);
        saveColumnConfig(resizedColumns);
    }

    const onDragStart = (e, column, index) => {
        setDragColumn(column);
        e.dataTransfer.effectAllowed = "move";
        const dragItem = e.target.parentNode.parentNode;
        e.dataTransfer.setData("text/html", dragItem);
        e.dataTransfer.setDragImage(dragItem, $(dragItem).outerWidth() - 10, $(dragItem).outerHeight() / 2);
    }

    const onDragEnd = (e) => {
        e.preventDefault();
        saveColumnConfig(columnsRef.current);
        setDragColumn(null);
    }

    const onDragOver = (e, column, index) => {
        if (dragColumn.dataIndex === column.dataIndex) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move"
        const listDrag = columns.filter(col => col.dataIndex !== dragColumn.dataIndex);
        listDrag.splice(index, 0, dragColumn);
        listDrag.map((col, index) => {
            col.order = index;
            return col;
        });
        setColumnVisible(listDrag, isResizeDisabled);
    }
    const onDragEnter = e => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move"
    }

    const onClickLockCol = () => {

    }

    const invisibleColCount = countBy(columns, 'visible')['false'];

    useEffect(() => {
        const tableKey = getTableKey();
        if (tableKey) {
            const isResizeDisabled = getColumnResizeDisabledConfigs(tableKey);
            setResizeDisabled(isResizeDisabled);
            const columnConfigs = getColumnVisibleConfigs(tableKey, columns);
            setColumnVisible(columnConfigs, isResizeDisabled);
        }
    }, [])
    const orderredColumns = orderBy(columns || [], ['order'], ['asc']);
    const renderColumns = orderredColumns.filter(col => col.visible !== false);
    const scroll = getResizableScroll(props.scroll, renderColumns, !!props.expandedRowRender);
    return (
        <Fragment>
            <div ref={tableRef} className={`row ml-0 mr-0 hms-list-component table-bg-white mt-${props.marginTop || 2} ${props.classParent ? props.classParent : ''}`}>
                <div className={`col-md-12 pl-0 pr-0 ${props.classChildren ? props.classChildren : ''}`}>
                    <div className={`ibox mb-${props.marginBottom || '0'}`}>
                        <div className="">
                            {props.componentTableList && (props.componentTableList.left !== undefined || props.componentTableList.right !== undefined) &&
                                <div className={`row mb-3`}>
                                    <div className={`col-md-6`}>
                                        {
                                            props.componentTableList.left
                                        }
                                    </div>
                                    <div className={`col-md-6 text-right`}>
                                        {
                                            props.componentTableList.right
                                        }
                                    </div>
                                </div>
                            }
                            {props.autoResize &&
                                <div className="table-actions">
                                    <Button size="small" icon="arrows-alt" onClick={onAutoResize}>{t(`action.autoResize`)}</Button>
                                </div>
                            }
                            <DndProvider backend={HTML5Backend}>
                                <AntTable {...props}
                                    locale={{
                                        emptyText: <Empty description={t('action.noData')} />,
                                        filterConfirm: <Button type="primary" icon="search" size="small"></Button>,
                                        filterReset: <Button type="danger" size="small">{t('action.cancel')}</Button>,
                                    }}
                                    components={components}
                                    columns={renderColumns}
                                    scroll={scroll}
                                    onChange={onChange}
                                    pagination={pagination}
                                    size={props.size !== undefined ? props.size : "middle"}
                                    bordered={props.bordered !== undefined ? props.bordered : false}
                                />
                            </DndProvider>
                        </div>
                    </div>

                </div>
                {!hideSettings &&
                    <Tooltip title={t(`notification.tableConfigVisible`)} placement="left">
                        <Button type="link" className="button-setting" onClick={() => setConfigVisible(!configVisibled)}>
                            <Icon type="setting"></Icon>
                        </Button>
                    </Tooltip>
                }
                <Drawer
                    title={t(`notification.tableConfigVisible`)}
                    placement="right"
                    onClose={() => onCloseSettings()}
                    visible={configVisibled}
                    bodyStyle={{ paddingBottom: 80 }}
                    width={345}
                >
                    <div className="mb-2">
                        <h3>{t(`notification.columnResizeLocked`)}</h3>
                        <Switch checked={isResizeDisabled} onChange={saveResizeDisabledConfig} />
                    </div>
                    <div className="flex-start direction-column">
                        <h3 className="mb-2">{t(`notification.columnVisibilities`)}</h3>
                        <ul className="column-setting-list" onDragOver={onDragEnter}>
                            {orderredColumns.map((col, index) => {
                                const checked = col.visible === false ? false : true;
                                const disabled = (invisibleColCount === columns.length - 1 && checked) || !!col.fixed;
                                return (
                                    <li key={col.key}
                                        onDragOver={(e) => !disabled && onDragOver(e, col, index)}
                                        onDragEnter={onDragEnter}
                                        className={classNames({
                                            'dragging': dragColumn && dragColumn.dataIndex === col.dataIndex
                                        })}
                                    >
                                        <Checkbox
                                            disabled={disabled}
                                            key={col.key}
                                            checked={checked}
                                            onChange={e => onColumnVisibleChange(col, e.target.checked)}
                                        >{col.title}</Checkbox>
                                        <div className="flex-center">
                                            {/* <div className="flex-center justify-center cursorPointer"
                                                style={{ width: 20 }}>
                                                <Tooltip title={t(`notification.${col.fixed ? 'unfixedColumn' : 'fixedColumn'}`)}>
                                                    <Icon component={() => <FontIcon icon={SolidIcons[col.fixed ? 'faLockOpen' : 'faLock']} />} />
                                                </Tooltip>
                                            </div> */}
                                            {!disabled ?
                                                <div
                                                    className="drag"
                                                    draggable
                                                    onDragStart={e => onDragStart(e, col, index)}
                                                    onDragEnd={onDragEnd}
                                                >
                                                    <Icon component={() => <FontIcon icon={SolidIcons.faGripLines} />} />
                                                </div>
                                                : <div style={{ width: 14, height: 14 }}></div>
                                            }
                                        </div>

                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            borderTop: '1px solid #e9e9e9',
                            padding: '10px 16px',
                            background: '#fff',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >

                        <Button icon="reload" onClick={onResetSetting} type="primary" ghost>{t(`action.reset`)}</Button>
                        <Button icon="check" onClick={onCloseSettings} type="primary">{t(`action.done`)}</Button>
                    </div>
                </Drawer>
            </div>
        </Fragment>
    )
};

Table.defaultProps = {
    hideSettings: false,
}

export default Table;

export const ResizeableTableTitle = props => {
    const { onResize, width, fixed, columnKey, ...restProps } = props;
    let thRef = createRef();
    const resizeHandle = (e, data) => {
        return onResize(e, data)
    }
    const resizeHandleStart = (e, data) => {
        const element = findDOMNode(thRef.current);
        const allWidthData = {};
        $(element).parent().children().each(function () {
            const siblingColumnKey = $(this).attr('data-column-key');
            if (siblingColumnKey !== undefined) {
                allWidthData[siblingColumnKey] = $(this).outerWidth();
            }
        });
        data.isStart = true;
        data.allWidth = allWidthData;
        resizeHandle(e, data);
    }
    const resizeHandleStop = (e, data) => {
        data.isStop = true;
        resizeHandle(e, data);
    }

    if (fixed || !onResize) {
        return <th {...(restProps)} data-column-key={columnKey} />
    }

    return (
        <Resizable
            width={width || 0}
            height={0}
            axis="x"
            onResize={resizeHandle}
            onResizeStart={resizeHandleStart}
            onResizeStop={resizeHandleStop}
            minConstraints={[MIN_COLUMN_WIDTH, 0]}
            draggableOpts={{ enableUserSelectHack: false, grid: [10, 10] }}
        >
            <th ref={thRef} {...restProps} data-column-key={columnKey} />
        </Resizable>
    );
};
